import React, { useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { IProfileDeatilsForm, IProfileInfo, ProfileParser } from './models';
import FormTextField from 'components/formComponents/FormTextField';
import { PopupOrPanel } from 'components/popupsAndPanels/PopupAndPanels';
import { ProfileApi } from 'api/profileAPI';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { useDispatch } from 'react-redux';
import { setUserInitials } from 'reducers/auth';
import { PASSWORD_REGEX } from 'appConstants';

type Props = {
  updateIsOpen: boolean;
  handleMenuClose: () => void;
  profileDetails: IProfileInfo;
  setProfileDetails: React.Dispatch<React.SetStateAction<IProfileInfo | null>>;
};

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  emailAddress: Yup.string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
  newPassword: Yup.string()
    .required('Password is required')
    .min(8, 'Password must contain between  8 and 20 characters')
    .max(20, 'Password must contain between  8 and 20 characters')
    .matches(
      PASSWORD_REGEX,
      'Password must contain at least one number and one letter'
    ),
  confirmNewPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
});

export const UpdateProfileForm: React.FC<Props> = ({
  updateIsOpen,
  handleMenuClose,
  profileDetails,
  setProfileDetails,
}) => {
  const dispatch = useDispatch();
  const [error, setError] = useState<string>('');

  const handleSubmit = async (
    values: IProfileDeatilsForm,
    { setSubmitting }: FormikHelpers<IProfileDeatilsForm>
  ) => {
    setSubmitting(true);
    const result = await ProfileApi.updateProfileDetails(values);
    if (result.data) {
      setProfileDetails(result.data);
      const userInitials = `${result.data.firstName.slice(
        0,
        1
      )}${result.data.lastName?.slice(0, 1)}`;
      dispatch(setUserInitials(userInitials.toUpperCase()));
      window.localStorage.setItem('UI', userInitials.toUpperCase());
      setSubmitting(false);
      handleMenuClose();
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={ProfileParser.createEmptyProfileDetails(profileDetails)}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
    >
      {({ values, submitForm, isSubmitting }) => (
        <PopupOrPanel
          isPopup={false}
          isOpen={updateIsOpen}
          isSubmitting={isSubmitting}
          position="right"
          hdrTitle="Update profile"
          primaryBtnTxt="Update"
          secondaryBtnTxt="Cancel"
          givenClass="ProfilePopoutMenu"
          handleClose={handleMenuClose}
          handlePrimaryBtn={submitForm}
          handleSecondaryBtn={handleMenuClose}
          width={560}
        >
          <FormGeneralError message={error} />
          <Form>
            <FormTextField
              label="First name"
              field="firstName"
              required={true}
              labelPosition="left"
              value={values.firstName}
            />
            <FormTextField
              label="Last name"
              field="lastName"
              required={true}
              labelPosition="left"
              value={values.lastName}
            />
            <FormTextField
              label="Email address"
              field="emailAddress"
              required={true}
              labelPosition="left"
              value={values.emailAddress}
            />
            <FormTextField
              label="Password"
              field="newPassword"
              type="password"
              labelPosition="left"
              required={true}
              value={values.newPassword}
            />
            <FormTextField
              label="Confirm password"
              field="confirmNewPassword"
              type="password"
              labelPosition="left"
              required={true}
              value={values.confirmNewPassword}
            />
          </Form>
        </PopupOrPanel>
      )}
    </Formik>
  );
};
